import axios from "axios";
import toast from "react-hot-toast";
import { processWithRetry } from "./processUtils";
import { getUploadMediaUrl } from "services/api/user.service";
import UploadProgress from "components/UI/UploadProgress";

export const uploadFile = async (file, presignedUrl) => {
  try {
    const id = `${file.name}-${Date.now()}`;
    const response = await axios.put(presignedUrl, file, {
      headers: {
        "Content-Type": file.type,
      },
      //   Optional: Uncomment if you want upload progress UI
      onUploadProgress: (progressEvent) => {
        const percentCompleted = Math.round(
          (progressEvent.loaded * 100) / (progressEvent.total ?? 1)
        );
        toast.custom(
          () => (
            <UploadProgress
              progress={percentCompleted}
              filename={file.name}
              filesize={formatFileSize(file.size)}
            />
          ),
          {
            id,
            icon: "📤",
            position: "bottom-right",
          }
        );
      },
    });

    if (response.status === 200) {
      // File uploaded successfully
      toast.dismiss(id);
    }
  } catch (error) {
    throw error;
  }
};

export const uploadMediaFiles = async ({
  files,
  toastId,
  foldername,
  fileLookUp,
  isPublic = false,
}) => {
  if (!files.length) return [];

  if (toastId) {
    toast.loading("Preparing media files", {
      id: toastId,
    });
  }

  const signedUrlResponses = await processWithRetry(
    files.map(
      (file) => () =>
        getUploadMediaUrl({
          filename: file.name,
          mimetype: file.type,
          filesize: file.size,
          foldername,
          isPublic,
        })
    ),
    {
      retries: 2,
      delay: (attempt) => attempt * 100,
    }
  );

  if (toastId) {
    toast.loading("Uploading media files", {
      id: toastId,
    });
  }

  const mediaDocs = signedUrlResponses
    .map((response) => response.data)
    .filter(Boolean);

  await processWithRetry(
    mediaDocs.map((doc) => async () => {
      try {
        if (!doc) return Promise.resolve();
        const targetFile = files.find((file) => file.name === doc.filename);
        if (!targetFile) return Promise.resolve();
        await uploadFile(targetFile, doc.signedUrl);
      } catch (error) {
        throw new Error(`Failed to upload file ${doc.filename}`);
      }
    }),
    {
      retries: 2,
      delay: (attempt) => attempt * 100,
    }
  );

  const result = {};

  mediaDocs.forEach((doc) => {
    const key =
      fileLookUp[
        generateFileFingerprint({
          name: doc.filename,
          size: doc.filesize,
          type: doc.mimetype,
        })
      ];
    result[key] = doc;
  });
  return result;
};

export function formatFileSize(bytes) {
  if (bytes === 0) return "0 Bytes";

  const units = ["Bytes", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  const size = bytes / Math.pow(1024, i);

  return `${size.toFixed(2)} ${units[i]}`;
}

export function generateFileLookUp(payload) {
  const fileLookUp = {};
  const files = [];
  Object.entries(payload).forEach(([key, value]) => {
    if (value instanceof File) {
      fileLookUp[generateFileFingerprint(value)] = key;
      files.push(value);
    }
  });
  return {
    fileLookUp,
    files,
  };
}

export function generateFileFingerprint(file) {
  return `${file.name}-${file.size}-${file.type}`;
}
